'use client';

import {createElement as h,useEffect,useId,useRef,useState} from 'react';
import {createSupportClient} from './client.mjs';
import {COPY} from './copy.js';

// Keying the complete session prevents even one render of another merchant's transcript.
export function SupportWidget(props) {
  return h(SessionWidget,{...props,key:props.identityKey});
}

function textWithSources(content) {
  if(typeof content!=='string') return null;
  return content.split(/(https:\/\/docs\.mupag\.com\.br\/[\w/?.=#%&-]+)/g).map((part,i)=>
    part.startsWith('https://docs.mupag.com.br/')
      ? h('a',{key:i,href:part,target:'_blank',rel:'noopener noreferrer'},part)
      : part);
}

function SessionWidget({locale='pt-BR',loadSession}) {
  const copy=COPY[locale]??COPY['pt-BR'];
  const panelId=useId();
  const [open,setOpen]=useState(false);
  const [view,setView]=useState('faq');
  const [mode,setMode]=useState(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [pollError,setPollError]=useState(false);
  const [olderError,setOlderError]=useState('');
  const [sessionExpired,setSessionExpired]=useState(false);
  const [uncertain,setUncertain]=useState(false);
  const [messages,setMessages]=useState([]);
  const [draft,setDraft]=useState('');
  const [canLoadOlder,setCanLoadOlder]=useState(true);
  const client=useRef(null);
  const lifecycle=useRef(null);
  const dialog=useRef(null);
  const launcher=useRef(null);
  const transcript=useRef(null);
  const sending=useRef(false);
  const draftRevision=useRef(0);
  const pendingRevision=useRef(null);
  const ended=useRef(false);

  useEffect(()=>{
    const controller=new AbortController();
    lifecycle.current=controller;
    return ()=>{controller.abort();client.current?.dispose();client.current=null;};
  },[]);

  useEffect(()=>{
    if(open) dialog.current?.showModal();
    else if(dialog.current?.open){dialog.current.close();launcher.current?.focus();}
  },[open]);

  function merge(list) {
    setMessages(previous=>{
      const byId=new Map(previous.map(m=>[m.id,m]));
      list.forEach(m=>{if(m&&!m.private&&[0,1].includes(m.message_type))byId.set(m.id,m);});
      return [...byId.values()].sort((a,b)=>a.id-b.id).slice(-500);
    });
  }

  function handleTerminal(error){
    if(error.code!=='session_expired'&&!ended.current)return false;
    ended.current=true;
    client.current?.dispose();client.current=null;
    setSessionExpired(true);setPollError(false);setOlderError('');
    return true;
  }

  useEffect(()=>{
    if(!open||view!=='chat'||sessionExpired) return;
    let stopped=false,timer,failures=0;
    const poll=async()=>{
      try {
        const list=await client.current?.messages();
        if(!stopped&&list){merge(list);setPollError(false);failures=0;}
      } catch(error) {
        if(!stopped&&!handleTerminal(error)){setPollError(true);failures=Math.min(failures+1,5);}
      }
      finally {if(!stopped&&!ended.current)timer=setTimeout(poll,Math.min(3000*2**failures,60000));}
    };
    poll();
    return ()=>{stopped=true;clearTimeout(timer);};
  },[open,view,sessionExpired]);

  useEffect(()=>{
    const node=transcript.current;
    if(node)node.scrollTop=node.scrollHeight;
  },[messages.at(-1)?.id,view,open]);

  async function start(nextMode) {
    if(sending.current||ended.current||uncertain)return;
    sending.current=true;setBusy(true);setError('');
    const signal=lifecycle.current.signal;
    try {
      if(!client.current){
        const session=await loadSession(signal);
        if(signal.aborted)return;
        client.current=createSupportClient(session);
      }
      const message=await client.current.start(nextMode,locale);
      if(signal.aborted)return;
      if(message)merge([message]);
      setMode(nextMode);setView('chat');
    } catch(error) {if(!signal.aborted&&!handleTerminal(error))setError(error.code==='rate_limited'?copy.rateLimited:copy.unavailable);}
    finally {if(!signal.aborted){sending.current=false;setBusy(false);}}
  }

  async function send(event) {
    event.preventDefault();
    if(sending.current||ended.current||uncertain||!draft.trim())return;
    sending.current=true;setBusy(true);setError('');
    const signal=lifecycle.current.signal;
    const submittedRevision=draftRevision.current;
    try {
      const message=await client.current.send(draft);
      if(!signal.aborted){
        merge([message]);
        if(submittedRevision===draftRevision.current)setDraft('');
      }
    } catch(error) {
      if(!signal.aborted&&!handleTerminal(error)){
        if(error.code==='delivery_uncertain'){pendingRevision.current=submittedRevision;setUncertain(true);}
        setError(({rate_limited:copy.rateLimited,sensitive_input:copy.sensitiveInput,invalid_message:copy.invalidMessage})[error.code]??copy.error);
      }
    }
    finally {if(!signal.aborted){sending.current=false;setBusy(false);}}
  }

  async function checkDelivery(){
    if(sending.current||ended.current||!uncertain)return;
    sending.current=true;setBusy(true);
    const signal=lifecycle.current.signal;
    try{
      const message=await client.current.reconcileSend();
      if(!signal.aborted){
        if(message)merge([message]);
        if(pendingRevision.current===draftRevision.current)setDraft('');
        pendingRevision.current=null;setUncertain(false);setError('');
      }
    }catch(error){if(!signal.aborted&&!handleTerminal(error))setError(error.code==='rate_limited'?copy.rateLimited:copy.uncertain);}
    finally{if(!signal.aborted){sending.current=false;setBusy(false);}}
  }

  async function older() {
    if(busy||ended.current||!messages.length)return;
    setBusy(true);setOlderError('');
    const signal=lifecycle.current.signal;
    try {
      const list=await client.current.messages(messages[0].id);
      if(!signal.aborted){merge(list);setCanLoadOlder(list.length>0);}
    } catch(error) {if(!signal.aborted&&!handleTerminal(error))setOlderError(error.code==='rate_limited'?copy.rateLimited:copy.olderError);}
    finally {if(!signal.aborted)setBusy(false);}
  }

  const button=(label,onClick,className='mps-button',disabled=false)=>h('button',{type:'button',className,onClick,disabled:busy||disabled},label);
  const docPrefix='https://docs.mupag.com.br/'+(locale==='en'?'en/':'')+'docs/';
  return h('div',{className:'mps-root','data-dd-privacy':'hidden','data-dd-action-name':'Support'},
    h('button',{ref:launcher,type:'button',className:'mps-launcher','aria-label':copy.open,'aria-expanded':open,'aria-controls':panelId,'aria-haspopup':'dialog',onClick:()=>setOpen(true)},
      h('svg',{width:21,height:21,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:1.8,'aria-hidden':true},h('path',{d:'M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9H13a8.5 8.5 0 0 1 8 8z'})),
      h('span',null,locale==='en'?'Help':'Ajuda')),
    h('dialog',{ref:dialog,id:panelId,className:'mps-panel','aria-labelledby':panelId+'-title',onCancel:()=>setOpen(false),onClose:()=>setOpen(false)},
      h('header',{className:'mps-header'},
        h('div',null,h('span',{className:'mps-brand'},'mupag / '+(locale==='en'?'support':'suporte')),
          h('h2',{id:panelId+'-title'},view==='faq'?copy.title:mode==='human'?copy.humanTitle:copy.aiTitle),
          h('p',null,view==='faq'?copy.subtitle:mode==='human'?copy.humanNote:copy.aiNote)),
        h('button',{type:'button',className:'mps-close','aria-label':copy.close,onClick:()=>setOpen(false)},'×')),
      view==='faq'
        ? h('div',{className:'mps-body'},
            h('h3',null,copy.faq),
            h('div',{className:'mps-faq'},copy.faqs.map(item=>h('details',{key:item.slug},h('summary',null,item.question),h('p',null,item.answer),h('a',{href:docPrefix+item.slug,target:'_blank',rel:'noopener noreferrer'},copy.docs)))),
            h('div',{className:'mps-actions'},mode
              ? button(sessionExpired?copy.viewConversation:copy.continueConversation,()=>setView('chat'),'mps-button mps-primary')
              : button(copy.ai,()=>start('ai'),'mps-button mps-primary',sessionExpired),
              mode!=='human'&&button(copy.human,()=>start('human'),'mps-button',sessionExpired||uncertain)))
        : h('div',{className:'mps-chat'},
            h('div',{className:'mps-chat-nav'},button(copy.back,()=>{setView('faq');setError('');},'mps-text-button'),mode!=='human'&&button(copy.human,()=>start('human'),'mps-text-button',sessionExpired||uncertain)),
            h('div',{className:'mps-transcript',ref:transcript,role:'log','aria-live':'polite','aria-relevant':'additions','aria-label':copy.support},
              messages.length>=20&&canLoadOlder&&messages.length<500&&button(copy.older,older,'mps-text-button',sessionExpired),
              olderError&&h('p',{className:'mps-status',role:'status'},olderError),
              messages.length===500&&h('p',{className:'mps-status',role:'status'},copy.historyLimit),
              messages.map(message=>h('div',{key:message.id,className:'mps-message '+(message.message_type===0?'mps-own':'')},
                h('span',{className:'mps-sender'},message.message_type===0?copy.you:copy.support),
                h('p',null,textWithSources(message.content)),
                message.attachments?.filter(a=>typeof a.data_url==='string'&&a.data_url.startsWith('https://')).map((a,i)=>h('a',{key:i,href:a.data_url,target:'_blank',rel:'noopener noreferrer'},copy.attachment))))),
            pollError&&h('p',{className:'mps-status',role:'status'},copy.pollError),
            uncertain&&!sessionExpired&&h('div',{className:'mps-delivery'},
              h('p',{className:'mps-status',role:'status'},copy.uncertain),
              button(copy.checkDelivery,checkDelivery,'mps-text-button')),
            h('form',{className:'mps-composer',onSubmit:send},
              h('label',{className:'mps-sr-only',htmlFor:panelId+'-input'},copy.input),
              h('textarea',{id:panelId+'-input',rows:2,maxLength:4000,value:draft,placeholder:copy.placeholder,onChange:event=>{draftRevision.current++;setDraft(event.target.value);}}),
              h('button',{className:'mps-button mps-primary',type:'submit',disabled:busy||uncertain||sessionExpired||!draft.trim(),'aria-label':copy.send},busy?'…':'↑'))),
      (sessionExpired||error)&&h('p',{className:'mps-error',role:'alert'},sessionExpired?copy.sessionExpired:error),
      busy&&view==='faq'&&h('p',{className:'mps-status',role:'status'},copy.loading),
      h('footer',{className:'mps-footer'},copy.privacy)));
}
